import { State } from "../state/state";

/**
 * Calculates the total auto-collect contribution from brick packs for a specific brick.
 * Returns both the total rate and details about each pack's contribution.
 */
export interface BrickPackAutoCollectDetail {
  packId: string;
  packTitle: string;
  autoPurchasesPerSecond: number;
  findPercentage: number; // 0-100
  effectiveRate: number; // autoPurchases * (findPercentage / 100)
}

export interface BrickPackAutoCollectResult {
  totalRate: number;
  totalAutoPurchases: number; // Total raw auto-purchases per second (not adjusted for find percentage)
  averageFindPercentage: number; // Weighted average across all packs (when auto-collect > 0)
  overallFindPercentage: number; // Average find percentage across ALL packs containing this brick (even without auto-collect)
  isPartOfPack: boolean; // True if brick is in any brick pack
  details: BrickPackAutoCollectDetail[];
}

/**
 * Calculates how much of a specific brick is being auto-collected via brick packs.
 *
 * @param brickId - The brick ID to calculate auto-collect for
 * @param state - The current game state
 * @returns Object with total rate, average find percentage, and per-pack details
 */
export function calculateBrickPackAutoCollect(
  brickId: string,
  state: State,
): BrickPackAutoCollectResult {
  const details: BrickPackAutoCollectDetail[] = [];
  let totalRate = 0;
  let totalAutoPurchases = 0;
  let weightedPercentageSum = 0;
  let totalFindPercentage = 0;
  let packCount = 0;

  // Iterate through all actions to find brick packs
  state.actions.forEach((action) => {
    // Skip non-brick pack actions
    if (!action.isBrickpack) return;

    // Check if this brick is in the pack's outcomes
    const outcomeIndex = action.outcome.findIndex((o) => o.brickId === brickId);
    if (outcomeIndex === -1) return; // Brick not in this pack

    // Calculate find percentage (base + upgrades)
    const basePercentage = action.outcome[outcomeIndex].baseChangeValue;
    const upgradeCount = state.brickPackUpgrades.get(action.id) || 0;
    const upgradeBonus = upgradeCount * 0.05;
    const findPercentage = Math.min(1.0, basePercentage + upgradeBonus);

    // Track overall find percentage (for all packs containing this brick)
    totalFindPercentage += findPercentage * 100;
    packCount++;

    // Get auto-collect rate for this pack (purchases per second)
    const autoPurchasesPerSecond =
      state.brickPackAutoCollect.get(action.id) || 0;

    // Only add to details if auto-collect is active
    if (autoPurchasesPerSecond > 0) {
      // Calculate effective rate (purchases/s * find%)
      const effectiveRate = autoPurchasesPerSecond * findPercentage;

      details.push({
        packId: action.id,
        packTitle: action.title,
        autoPurchasesPerSecond,
        findPercentage: findPercentage * 100, // Convert to percentage
        effectiveRate,
      });

      totalRate += effectiveRate;
      totalAutoPurchases += autoPurchasesPerSecond;
      weightedPercentageSum += effectiveRate * (findPercentage * 100);
    }
  });

  // Calculate weighted average find percentage (for auto-collect only)
  const averageFindPercentage =
    totalRate > 0 ? weightedPercentageSum / totalRate : 0;

  // Calculate overall average find percentage (for all packs, even without auto-collect)
  const overallFindPercentage =
    packCount > 0 ? totalFindPercentage / packCount : 0;

  return {
    totalRate,
    totalAutoPurchases,
    averageFindPercentage,
    overallFindPercentage,
    isPartOfPack: packCount > 0,
    details,
  };
}
